// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * EISCOVEO-67
 * Just parse locationHref and referringUrl to the BE api on every get products api call
 */

import { trackSearchEvent } from "@eisinc/App/CoveoConnector/Services/CoveoService";
import { xCoveoState } from "@eisinc/App/CoveoConnector/Store/Reducers";
import { getQueryFromUrl } from "@eisinc/App/CoveoConnector/Store/Selectors/CoveoSelectors";
import { SearchEventRequest } from "@eisinc/App/CoveoConnector/Types/schema";
import { XngExecuteOnSuccessCallback } from "@eisinc/App/Handlers/Data/Products/LoadProducts";
import { addToChainAfter, addToEndOfChain } from "@insite/client-framework/HandlerCreator";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    chain,
    ExecuteOnSuccessCallback,
    PopulateApiParameter,
} from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";

export const CoveoPopulateApiParameter: typeof PopulateApiParameter = props => {
    const state = props.getState() as xCoveoState;

    if (state.xCoveo?.trackCoveo) {
        (props.apiParameter as any).locationHref = document.location.href;
        (props.apiParameter as any).referringUrl = document.referrer;
        (props.apiParameter as any).searchHub = (props.parameter as any).searchHub || "listing";
    }
};

/**
 * Store analyticsObject in redux store in order to use it on PDP
 * @param props
 */
export const CoveoStoreAnalyticsObject: typeof ExecuteOnSuccessCallback = props => {
    const state = props.getState() as xCoveoState;
    const searchPath = getSettingsCollection(state).searchSettings.searchPath;
    const isSearchPath = !!getLocation(state).pathname.toLowerCase().startsWith(`/${searchPath.toLowerCase()}`);
    const searchHub = isSearchPath ? "search" : "listing";

    if (state.xCoveo?.trackCoveo && (props.apiResult as any).analyticsObject) {
        props.dispatch({
            type: "xCoveo/storeSearchAnalyticsObject",
            analyticsObject: (props.apiResult as any).analyticsObject,
            query: (props.apiResult as any).analyticsObject.queryText,
            searchHub,
        } as any);
    }
};

/**
 * Send facetDeselect event if a user deselected a facet
 * We can't use RemoveProductFilters handler to send it
 * because we don't have total items count there
 * @param props
 */
export const CoveoSendFacetDeselectEvent: typeof ExecuteOnSuccessCallback = async props => {
    const state = props.getState() as xCoveoState;
    if (state.xCoveo?.trackCoveo && state.xCoveo.lastFacetDeSelectEvent) {
        const facetDeselectEventParameter = JSON.parse(JSON.stringify(state.xCoveo.lastFacetDeSelectEvent)); // Deep clone
        const analyticsObject = (props.apiResult as any).analyticsObject;

        facetDeselectEventParameter.numberOfResults = props.apiResult.pagination?.totalItemCount;
        facetDeselectEventParameter.responseTime = analyticsObject.responseTime;
        facetDeselectEventParameter.searchQueryUid = analyticsObject.searchQueryUid || translate("my-uid");

        props.dispatch({
            type: "xCoveo/setLastFacetDeSelectEvent",
            lastFacetDeSelectEvent: undefined,
        } as any);

        await trackSearchEvent(facetDeselectEventParameter);

        props.dispatch({
            type: "xWidgetsCoveo/SetWidgetIdAndData",
            widgetId: "PLP",
            analyticsObject,
        } as any);
    }
};

/**
 * Send didYouMean click event
 * @param props
 */
export const CoveoSendDidYouMeanClickEvent: typeof ExecuteOnSuccessCallback = async props => {
    const state = props.getState() as xCoveoState;
    if (state.xCoveo?.trackCoveo && state.xCoveo.lastDidyoumeanClickEvent) {
        const didyoumeanClickEvent = JSON.parse(JSON.stringify(state.xCoveo.lastDidyoumeanClickEvent)); // Deep clone
        didyoumeanClickEvent.numberOfResults = props.apiResult.pagination?.totalItemCount;
        didyoumeanClickEvent.responseTime = (props.apiResult as any).analyticsObject.responseTime;

        props.dispatch({
            type: "xCoveo/setLastDidyoumeanClickEvent",
            didyoumeanClickEvent: undefined,
        } as any);

        await trackSearchEvent(didyoumeanClickEvent);
    }
};

/**
 * Send searchboxSubmit event if a user searched for a term
 * @param props
 */
export const CoveoSendSearchBoxEvent: typeof ExecuteOnSuccessCallback = async props => {
    const state = props.getState() as xCoveoState;
    const analyticsObject = (props.apiResult as any).analyticsObject;
    if (state.xCoveo?.trackCoveo && state.xCoveo.searchEventParameters && analyticsObject) {
        const searchEventParameters = JSON.parse(JSON.stringify(state.xCoveo.searchEventParameters)); // Deep clone
        searchEventParameters.numberOfResults = props.apiResult.pagination?.totalItemCount;
        searchEventParameters.responseTime = analyticsObject.responseTime;
        searchEventParameters.searchQueryUid = analyticsObject.searchQueryUid || translate("my-uid");
        searchEventParameters.advancedQuery = analyticsObject.advancedQuery;
        searchEventParameters.queryPipeline = analyticsObject.queryPipeline;

        // reset BEFORE the await, not after — makes the check-and-clear atomic.
        // Whichever concurrent loadProducts call reaches here first is the one that sends
        // the event; every other one, regardless of when it resolves, sees this already
        // cleared and correctly skips.
        props.dispatch({
            type: "xCoveo/setSearchboxSubmitEvent",
            searchEventParameters: undefined,
        } as any);

        await trackSearchEvent(searchEventParameters);

        // write PLP widget analytics from this SAME call's analyticsObject,
        // so the click event's searchQueryUid always matches the search event that was
        // actually sent, rather than whatever the last-resolving loadProducts call wrote.
        props.dispatch({
            type: "xWidgetsCoveo/SetWidgetIdAndData",
            widgetId: "PLP",
            analyticsObject,
        } as any);
    }
};

/**
 * Send facets/sorting events
 * @param props
 */
export const CoveoSendFacetSelectSearchBoxEvent: typeof ExecuteOnSuccessCallback = async props => {
    const state = props.getState() as xCoveoState;

    if (state.xCoveo?.trackCoveo && state.xCoveo.facetSelectEventParameter) {
        const facetEventParameters = JSON.parse(JSON.stringify(state.xCoveo.facetSelectEventParameter)); // Deep clone
        const analyticsObject = (props.apiResult as any).analyticsObject;

        facetEventParameters.numberOfResults = props.apiResult.pagination?.totalItemCount;
        facetEventParameters.responseTime = analyticsObject.responseTime;
        facetEventParameters.searchQueryUid = analyticsObject.searchQueryUid || translate("my-uid");
        facetEventParameters.advancedQuery = analyticsObject.advancedQuery;
        facetEventParameters.queryPipeline = analyticsObject.queryPipeline;

        props.dispatch({
            type: "xCoveo/setfacetSelectEvent",
            facetSelectEventParameter: undefined,
        } as any);

        await trackSearchEvent(facetEventParameters);

        props.dispatch({
            type: "xWidgetsCoveo/SetWidgetIdAndData",
            widgetId: "PLP",
            analyticsObject,
        } as any);
    }
};

/**
 * Send facetClearAll event if a user clicked on clear all
 */
export const CoveoSendFacetClearAllEvent: typeof ExecuteOnSuccessCallback = async props => {
    const state = props.getState() as xCoveoState;
    const searchPath = getSettingsCollection(state).searchSettings.searchPath;
    const isSearchPath = !!getLocation(state).pathname.toLowerCase().startsWith(`/${searchPath.toLowerCase()}`);
    const searchHub = isSearchPath ? "search" : "listing";

    if (state.xCoveo?.trackCoveo && state.xCoveo.facetClearAllTriggered) {
        const pagination = getProductListDataViewProperty(state, "pagination");
        const analyticsObject = (props.apiResult as any).analyticsObject;
        const clearAllFacetsEventParameter: SearchEventRequest = {
            actionCause: "facetClearAll",
            numberOfResults: pagination?.totalItemCount || 0,
            queryText: getQueryFromUrl(state).query || "",
            responseTime: analyticsObject?.responseTime || 0,
            searchQueryUid: analyticsObject?.searchQueryUid || translate("my-uid"),
            originLevel1: searchHub,
            searchHub,
        };

        props.dispatch({
            type: "xCoveo/setFacetClearAllTriggered",
            facetClearAllTriggered: false,
        } as any);

        await trackSearchEvent(clearAllFacetsEventParameter);

        if (analyticsObject) {
            props.dispatch({
                type: "xWidgetsCoveo/SetWidgetIdAndData",
                widgetId: "PLP",
                analyticsObject,
            } as any);
        }
    }
};

export const CoveoSendInterfaceLoadEvent: typeof ExecuteOnSuccessCallback = async props => {
    /**
     * Track interfaceLoad if a user navigated to a fresh Search Listing Page
     * shouldSendInterfaceLoad starts as true meaning the current user in the current session
     * didn't visit the Search Listing Page yet
     */
    const state = props.getState() as xCoveoState;
    const analyticsObject = state.xCoveo.analyticsObject;
    if (
        state.xCoveo?.trackCoveo &&
        analyticsObject &&
        state.xCoveo.shouldSendInterfaceLoad &&
        getQueryFromUrl(state).query
    ) {
        props.dispatch({
            type: "xCoveo/updateShouldSendInterfaceLoad",
            shouldSendInterfaceLoad: false,
        } as any);

        const pagination = getProductListDataViewProperty(state, "pagination");
        const interfaceLoadParameters: SearchEventRequest = {
            actionCause: "interfaceLoad",
            numberOfResults: pagination?.totalItemCount || 0,
            queryText: getQueryStrings(state).query as string,
            responseTime: analyticsObject.responseTime || 0,
            searchQueryUid: analyticsObject.searchQueryUid || translate("my-uid"),
            originLevel1: "search",
            searchHub: "search",
        };

        await trackSearchEvent(interfaceLoadParameters);
    }
};

export const CoveoSendSearchFromLinkEvent: typeof ExecuteOnSuccessCallback = async props => {
    /**
     * Track searchFromLink when a user navigate to Category or Brand Listing Page
     */
    const state = props.getState() as xCoveoState;
    const analyticsObject = (props.apiResult as any).analyticsObject;
    const searchPath = getSettingsCollection(state).searchSettings.searchPath;
    const isSearchPath = !!getLocation(state).pathname.toLowerCase().startsWith(`/${searchPath.toLowerCase()}`);
    const searchHub = isSearchPath ? "search" : "listing";

    if (
        state.xCoveo?.trackCoveo &&
        state.xCoveo.shouldSendSearchFromLinkEvent &&
        (props.apiParameter as any).searchHub !== "recs" &&
        (props.apiResult as any)?.statusCode !== 304 &&
        analyticsObject
    ) {
        const pagination = getProductListDataViewProperty(state, "pagination");
        const searchFromLinkParameters: SearchEventRequest = {
            actionCause: "searchFromLink",
            numberOfResults: pagination?.totalItemCount || 0,
            queryText: "",
            responseTime: analyticsObject.responseTime || 0,
            searchQueryUid: analyticsObject.searchQueryUid || translate("my-uid"),
            originLevel1: searchHub,
            searchHub,
        };
        // reset shouldSendSearchFromLinkEvent
        props.dispatch({
            type: "xCoveo/setShouldSendSearchFromLinkEvent",
            shouldSendSearchFromLinkEvent: false,
        } as any);

        await trackSearchEvent(searchFromLinkParameters);
    }
};

export const CoveoSendSearchEvent: typeof ExecuteOnSuccessCallback = async props => {
    /**
     * Send search event for product carousels
     */
    const state = props.getState() as xCoveoState;
    const analyticsObject = (props.apiResult as any).analyticsObject;
    if (state.xCoveo?.trackCoveo && (props.apiParameter as any).searchHub === "recs" && analyticsObject) {
        const pagination = getProductListDataViewProperty(state, "pagination");
        const searchFromLinkParameters: SearchEventRequest = {
            actionCause: "searchFromLink",
            numberOfResults: pagination?.totalItemCount || 0,
            queryText: "",
            responseTime: analyticsObject.responseTime || 0,
            searchQueryUid: analyticsObject.searchQueryUid || translate("my-uid"),
            originLevel1: "listing",
            searchHub: "listing",
        };

        await trackSearchEvent(searchFromLinkParameters);
    }
};

addToChainAfter(chain, PopulateApiParameter, CoveoPopulateApiParameter);
// This chain order is specific to EIS
addToChainAfter(chain, XngExecuteOnSuccessCallback, CoveoSendFacetDeselectEvent);
addToChainAfter(chain, CoveoSendFacetDeselectEvent, CoveoSendDidYouMeanClickEvent);
addToChainAfter(chain, CoveoSendDidYouMeanClickEvent, CoveoSendSearchBoxEvent);
addToChainAfter(chain, CoveoSendSearchBoxEvent, CoveoSendFacetSelectSearchBoxEvent);
addToChainAfter(chain, CoveoSendFacetSelectSearchBoxEvent, CoveoSendFacetClearAllEvent);
addToChainAfter(chain, CoveoSendFacetClearAllEvent, CoveoSendInterfaceLoadEvent);
addToChainAfter(chain, CoveoSendInterfaceLoadEvent, CoveoSendSearchFromLinkEvent);
addToChainAfter(chain, CoveoSendSearchFromLinkEvent, CoveoSendSearchEvent);
addToEndOfChain(chain, CoveoStoreAnalyticsObject);
